import React, { useEffect, useState } from 'react';
import './App.css';
import PageLayout from "./containers/PageLayout/PageLayout";
import LoginPage from "./containers/LoginPage/LoginPage";
import ChangePassword from "./containers/ChangePassword/ChangePassword";
import ResetPassword from "./containers/ResetPassword/ResetPassword";
import Loader from './components/Loader/Loader';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectIsAuthenticated,
    selectLoadingState,
    selectAuthChecked,
    selectMustChangePassword,
    restoreSession,
    sessionLost
} from './store/pageLayoutSlice';
import { setSessionLostHandler } from './APICalls/APICalls';

function App() {

    const dispatch = useDispatch();

    const isAuthenticated = useSelector( selectIsAuthenticated );
    const inProgress = useSelector( selectLoadingState );
    const authChecked = useSelector( selectAuthChecked );
    const mustChangePassword = useSelector( selectMustChangePassword );

    // There is no router in this app, so the one URL that has to mean
    // something on its own is read here. Held in state rather than read on
    // every render because clearing it is what returns the user to the login
    // form once they are finished.
    const [ resetToken, setResetToken ] = useState(() => {
        return new URLSearchParams( window.location.search ).get('resetToken') || '';
    });

    function clearResetToken() {
        setResetToken('');
        // Takes the token out of the address bar and out of history, so it is
        // not sitting in the URL of a shared or logged-in browser afterwards.
        window.history.replaceState( {}, document.title, window.location.pathname );
    }

    useEffect(() => {
        // Any request can come back 401 if the session ended while the user was
        // working. Handling it in one place returns them to the login screen
        // rather than leaving a half-dead page behind.
        setSessionLostHandler( payload => dispatch( sessionLost( payload ) ) );

        // The client stores no identity of its own, so on every load the only
        // way to know whether we are signed in is to ask the server.
        dispatch( restoreSession() );
    }, [ dispatch ]);

    // Until that check comes back we genuinely do not know which screen is
    // correct. Showing the login form in the meantime would flash it at users
    // who are already signed in, every refresh.
    if ( !authChecked ) {
        return (
            <div className="App">
                <Loader />
            </div>
        );
    }

    let content;
    if ( resetToken ) {
        // Takes precedence over everything, including an existing session: a
        // user who is already signed in somewhere may still be following a
        // reset link, and completing it ends that session anyway.
        content = <ResetPassword token={ resetToken } onFinished={ clearResetToken } />;
    } else if ( !isAuthenticated ) {
        content = <LoginPage />;
    } else if ( mustChangePassword ) {
        // The server refuses every other endpoint for this account, so there
        // is nothing else worth rendering.
        content = <ChangePassword />;
    } else {
        content = <PageLayout />;
    }

    return (
        <div className="App">
            { content }
            { inProgress === 'loading' && <Loader /> }
        </div>
    );
}

export default App;
